// ─── ONBOARDING / TOUR "SEEN" FLAGS ───────────────────────────────────────────
// Kept in localStorage (same pattern as the rest of the app's per-device state)
// so this is instant to read/write and needs no sign-in. resetOnboardingAndTour
// is what the Settings "Replay" button calls to re-trigger both without needing
// a fresh account.
import { load, save } from "../utils/storage";

const ONBOARDING_DONE_KEY = "clarity_onboardingDone";
const ONBOARDING_ANSWERS_KEY = "clarity_onboardingAnswers";
const TOUR_DONE_KEY = "clarity_tourDone";
const PLANNER_TOUR_DONE_KEY = "clarity_plannerTourDone";
const PLANNER_EXTRAS_TOUR_DONE_KEY = "clarity_plannerExtrasTourDone";
const CLOCKIN_TOUR_DONE_KEY = "clarity_clockInTourDone";
const CLOCKIN_CHART_TOUR_DONE_KEY = "clarity_clockInChartTourDone";
const JOURNAL_TOUR_DONE_KEY = "clarity_journalTourDone";
const SPACES_TOUR_DONE_KEY = "clarity_spacesTourDone";
// The one-step guides on a Full Stop / Limited habit's shield chip (Jade, 2026-10-26).
const FULLSTOP_TOUR_DONE_KEY = "clarity_fullStopTourDone";
const LIMITED_TOUR_DONE_KEY = "clarity_limitedTourDone";
// Set the first time the user ever logs a habit completion (any checked
// checkbox, counter increment, or instance log — see HabitsTab.jsx's
// logHabit). One of the prerequisites for the two Habit Stats scroll
// coach marks below: they shouldn't pop up over a page with nothing on it
// yet to explain.
const HAS_TICKED_HABIT_KEY = "clarity_hasTickedHabit";
// The Target Completion / Overall Completion sections on the Habit Stats
// sub-page (ProfileTab.jsx) are Premium-only, so their coach marks are
// scroll-triggered rather than tab-open-triggered — a tab-open tour would
// fire before the user has scrolled anywhere near them. Each fires once,
// ever, the first time its section scrolls into view; Overall Completion's
// stays gated behind Target Completion's own "done" flag so the two never
// show out of order even if the user scrolls past both quickly.
const TARGET_COMPLETION_GUIDE_DONE_KEY = "clarity_targetCompletionGuideDone";
const OVERALL_COMPLETION_GUIDE_DONE_KEY = "clarity_overallCompletionGuideDone";
// In-progress draft of the quiz itself (current screen + answers so far).
// Needed because Section 2's Google Calendar checkbox triggers a real, full-
// page OAuth redirect — without this, the round trip back would land the user
// at the very start of the quiz with everything they'd entered lost.
const ONBOARDING_DRAFT_KEY = "clarity_onboardingDraft";

export const isOnboardingDone = () => load(ONBOARDING_DONE_KEY, false) === true;
export const getOnboardingAnswers = () => load(ONBOARDING_ANSWERS_KEY, {});
export const markOnboardingDone = (answers) => {
  save(ONBOARDING_ANSWERS_KEY, answers);
  save(ONBOARDING_DONE_KEY, true);
};

export const isTourDone = () => load(TOUR_DONE_KEY, false) === true;
export const markTourDone = () => save(TOUR_DONE_KEY, true);

// Per-tab tours (Planner, Clock-In) — each triggered once, the first time the
// user opens that tab, independently of the main post-onboarding tour above.
export const isPlannerTourDone = () => load(PLANNER_TOUR_DONE_KEY, false) === true;
export const markPlannerTourDone = () => save(PLANNER_TOUR_DONE_KEY, true);
export const isPlannerExtrasTourDone = () => load(PLANNER_EXTRAS_TOUR_DONE_KEY, false) === true;
export const markPlannerExtrasTourDone = () => save(PLANNER_EXTRAS_TOUR_DONE_KEY, true);
export const isClockInTourDone = () => load(CLOCKIN_TOUR_DONE_KEY, false) === true;
export const markClockInTourDone = () => save(CLOCKIN_TOUR_DONE_KEY, true);
export const isClockInChartTourDone = () => load(CLOCKIN_CHART_TOUR_DONE_KEY, false) === true;
export const markClockInChartTourDone = () => save(CLOCKIN_CHART_TOUR_DONE_KEY, true);
export const isJournalTourDone = () => load(JOURNAL_TOUR_DONE_KEY, false) === true;
export const markJournalTourDone = () => save(JOURNAL_TOUR_DONE_KEY, true);
export const isSpacesTourDone = () => load(SPACES_TOUR_DONE_KEY, false) === true;
export const markSpacesTourDone = () => save(SPACES_TOUR_DONE_KEY, true);
export const isFullStopTourDone = () => load(FULLSTOP_TOUR_DONE_KEY, false) === true;
export const markFullStopTourDone = () => save(FULLSTOP_TOUR_DONE_KEY, true);
export const isLimitedTourDone = () => load(LIMITED_TOUR_DONE_KEY, false) === true;
export const markLimitedTourDone = () => save(LIMITED_TOUR_DONE_KEY, true);

export const hasTickedHabit = () => load(HAS_TICKED_HABIT_KEY, false) === true;
export const markHabitTicked = () => save(HAS_TICKED_HABIT_KEY, true);

export const isTargetCompletionGuideDone = () => load(TARGET_COMPLETION_GUIDE_DONE_KEY, false) === true;
export const markTargetCompletionGuideDone = () => save(TARGET_COMPLETION_GUIDE_DONE_KEY, true);
export const isOverallCompletionGuideDone = () => load(OVERALL_COMPLETION_GUIDE_DONE_KEY, false) === true;
export const markOverallCompletionGuideDone = () => save(OVERALL_COMPLETION_GUIDE_DONE_KEY, true);

// Per-tab tours re-mount from scratch every time their tab is reopened (the
// trigger effects in App.jsx tear the tour down the moment the user leaves
// the tab, since a step's target only exists on that one tab). Without
// remembering how far the user actually got, any tour that takes more than
// one visit to finish — Journal's 5 steps, several of them requiring a real
// action first (write something, open the folder picker, make a folder) —
// restarts at step 1 every single time, forever, since it's interrupted
// before ever reaching the last step that would mark it done. Saved under
// its own key per tour (not the "done" key) and read back in on mount so
// CoachMarkTour can resume instead of restart; cleared once the tour
// actually finishes.
const TOUR_STEP_KEY_PREFIX = "clarity_tourStep_";
export const getTourStep = (tourKey) => load(TOUR_STEP_KEY_PREFIX + tourKey, 0);
export const saveTourStep = (tourKey, index) => save(TOUR_STEP_KEY_PREFIX + tourKey, index);
export const clearTourStep = (tourKey) => save(TOUR_STEP_KEY_PREFIX + tourKey, 0);

export const loadOnboardingDraft = () => load(ONBOARDING_DRAFT_KEY, null);
export const saveOnboardingDraft = (draft) => save(ONBOARDING_DRAFT_KEY, draft);
export const clearOnboardingDraft = () => save(ONBOARDING_DRAFT_KEY, null);

export const resetOnboardingAndTour = () => {
  save(ONBOARDING_DONE_KEY, false);
  save(TOUR_DONE_KEY, false);
  save(PLANNER_TOUR_DONE_KEY, false);
  save(PLANNER_EXTRAS_TOUR_DONE_KEY, false);
  save(CLOCKIN_TOUR_DONE_KEY, false);
  save(CLOCKIN_CHART_TOUR_DONE_KEY, false);
  save(JOURNAL_TOUR_DONE_KEY, false);
  save(SPACES_TOUR_DONE_KEY, false);
  save(FULLSTOP_TOUR_DONE_KEY, false);
  save(LIMITED_TOUR_DONE_KEY, false);
  // Not hasTickedHabit — that's a real fact about the account ("has this
  // person ever logged a completion"), not a "have they seen this guide
  // panel yet" flag, so replaying the guide shouldn't force them to re-tick
  // a habit just to see these two again.
  save(TARGET_COMPLETION_GUIDE_DONE_KEY, false);
  save(OVERALL_COMPLETION_GUIDE_DONE_KEY, false);
  clearTourStep("planner");
  clearTourStep("plannerExtras");
  clearTourStep("clockin");
  clearTourStep("journal");
  clearTourStep("spaces");
  clearTourStep("fullStop");
  clearTourStep("limited");
  clearOnboardingDraft();
};
